import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Utensils,
  Phone,
  Target,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Heart,
  Clock,
  MessageCircle,
} from "lucide-react";
import Footer from "../components/Footer";

// Google Icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("login") === "true") {
      setIsLoginModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const loggedInUser = await loginWithGoogle();
      toast.success(`Welcome${loggedInUser?.name ? `, ${loggedInUser.name}` : ""}!`);
      if (loggedInUser.onboarding_complete) {
        navigate("/dashboard");
      } else {
        navigate("/onboarding");
      }
    } catch (error) {
      const message = error?.message || "Google sign-in failed. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
      setIsLoginModalOpen(false);
    }
  };

  const handleCtaClick = () => {
    if (user) {
      if (user.onboarding_complete) {
        navigate("/dashboard");
      } else {
        navigate("/onboarding");
      }
    } else {
      setIsLoginModalOpen(true);
    }
  };

  const features = [
    {
      icon: <Utensils className="w-6 h-6" />,
      title: "Indian-Friendly Diet Plans",
      description:
        "Personalized meal plans with home-style foods like dal, roti, sabzi - foods you actually love.",
      className: "md:col-span-2",
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "AI Accountability Calls",
      description:
        "Friendly Hinglish calls to check on your progress and keep you motivated.",
      className: "md:col-span-2",
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Goal Tracking",
      description: "Track your journey towards your target weight with simple nutrition logging.",
      className: "md:col-span-2",
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "24/7 AI Support",
      description:
        "Chat anytime for diet advice, meal suggestions, or motivation.",
      className: "md:col-span-2",
    },
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      location: "Mumbai",
      text: "Finally a diet plan that understands Indian food! I've lost 5 kg in 2 months without giving up my chai and rotis.",
      avatar: "P",
    },
    {
      name: "Aarav Patel",
      location: "Bangalore",
      text: "The accountability calls are a game-changer. It's like having a supportive friend checking in on you daily.",
      avatar: "A",
    },
    {
      name: "Meera Reddy",
      location: "Hyderabad",
      text: "Simple, practical advice that fits my busy schedule. The AI chat helps me make better food choices every day.",
      avatar: "M",
    },
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Quick Onboarding",
      description: "Tell us about your goals, preferences, and schedule in 2 minutes.",
    },
    {
      step: "02",
      title: "Get Your Plan",
      description: "Receive a personalized diet plan tailored to Indian cuisine.",
    },
    {
      step: "03",
      title: "Stay Accountable",
      description: "Get friendly AI calls to track your progress and stay motivated.",
    },
  ];

  return (
    <div className="min-h-screen bg-background relative">
      {/* Subtle noise texture */}
      <div className="fixed inset-0 opacity-[0.02] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIgb3BhY2l0eT0iMC4wNSIvPjwvc3ZnPg==')]"></div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <img src="/logo.svg" alt="Nutri Saathi Logo" className="w-10 h-10 object-contain" />
            </div>
            <span className="font-bold text-xl text-foreground">Nutri Saathi</span>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Button
                onClick={() => navigate(user.onboarding_complete ? "/dashboard" : "/onboarding")}
                data-testid="dashboard-nav-btn"
                className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-full px-6 btn-primary"
              >
                Go to Dashboard
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => setIsLoginModalOpen(true)}
                data-testid="login-nav-btn"
                className="text-foreground hover:text-primary font-medium"
              >
                Login
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                AI-Powered Nutrition Coaching
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                Your Personal
                <br />
                <span className="text-primary">Diet Coach</span>
                <br />
                for Indian Lifestyle
              </h1>
              <p className="text-base lg:text-lg text-muted-foreground max-w-xl">
                Get personalized Indian diet plans and friendly AI accountability calls
                that keep you on track. No gym bro advice - just practical,
                home-style nutrition that works.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={handleCtaClick}
                  data-testid="hero-cta-btn"
                  className="bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-full px-8 py-6 text-lg btn-primary pulse-soft"
                >
                  {user ? "Go to Dashboard" : "Start Your Journey"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {["P", "A", "M", "R"].map((letter, i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center text-primary font-medium text-sm"
                    >
                      {letter}
                    </div>
                  ))}
                </div>
                <div className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">500+</span> Indians
                  already on their journey
                </div>
              </div>
            </div>

            <div className="relative fade-in stagger-2 hidden lg:block">
              <div className="relative">
                <img
                  src="/images/indian-meal.jpg"
                  alt="Healthy Indian meal"
                  className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/5]"
                />
                {/* Floating cards */}
                <Card className="absolute -left-8 top-20 bg-white/95 backdrop-blur shadow-xl animate-bounce-slow">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Diet Plan Ready</p>
                      <p className="text-sm text-muted-foreground">Personalized for you</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="absolute -right-4 bottom-32 bg-white/95 backdrop-blur shadow-xl">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <Phone className="w-6 h-6 text-[#D48C70]" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Next Call: 9 AM</p>
                      <p className="text-sm text-muted-foreground">Stay accountable</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-secondary/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Everything You Need to
              <span className="text-primary"> Stay Healthy</span>
            </h2>
            <p className="text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
              A complete wellness system designed for busy Indian professionals
              who want practical, sustainable results.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card
                key={index}
                className={`card-hover border-border/50 ${feature.className}`}
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Get Started in
              <span className="text-primary"> 3 Simple Steps</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="text-center space-y-4 fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-20 h-20 mx-auto rounded-3xl bg-primary/10 flex items-center justify-center">
                  <span className="text-3xl font-bold text-primary">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-foreground">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 bg-primary/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 fade-in">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Loved by
              <span className="text-primary"> Real Indians</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="card-hover border-border/50"
                data-testid={`testimonial-card-${index}`}
              >
                <CardContent className="p-6 space-y-4">
                  <p className="text-muted-foreground italic font-serif">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-border/50">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Login Modal */}
      <Dialog open={isLoginModalOpen} onOpenChange={setIsLoginModalOpen}>
        <DialogContent className="max-w-md p-8 rounded-2xl border border-border/50 bg-[#F9F7F2] shadow-xl text-foreground">
          <DialogHeader className="space-y-3 text-center flex flex-col items-center">
            <div className="w-16 h-16 flex items-center justify-center bg-white rounded-2xl shadow-sm border border-border/20 p-2">
              <img src="/logo.svg" alt="Nutri Saathi Logo" className="w-12 h-12 object-contain" />
            </div>
            <DialogTitle className="text-2xl font-bold text-[#1A2D26]">Welcome to Nutri Saathi</DialogTitle>
            <DialogDescription className="text-muted-foreground text-center text-sm max-w-xs">
              Your AI-powered Indian nutrition partner. Sign in to start your journey.
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 flex flex-col items-center">
            <Button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 rounded-xl h-12 shadow-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-95 hover:-translate-y-0.5"
              disabled={isLoading}
              data-testid="google-signin-btn"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#1A2D26] border-t-transparent"></div>
              ) : (
                <>
                  <GoogleIcon />
                  Continue with Google
                </>
              )}
            </Button>
          </div>

          <div className="text-xs text-center text-muted-foreground leading-relaxed pt-4 border-t border-border/40">
            By continuing, you agree to our{" "}
            <Link to="/terms" onClick={() => setIsLoginModalOpen(false)} className="underline hover:text-primary transition-colors">
              Terms & Conditions
            </Link>{" "}
            and{" "}
            <Link to="/privacy" onClick={() => setIsLoginModalOpen(false)} className="underline hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            . Please also review our{" "}
            <Link to="/disclaimer" onClick={() => setIsLoginModalOpen(false)} className="underline hover:text-primary transition-colors">
              Medical Disclaimer
            </Link>
            .
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
